import { nextTick } from 'vue';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';
import { useCheckoutStore } from '~~/common/stores/checkout';
import { useDeliveryStore } from '~~/shop/app/stores/delivery';
import { useNuxtApp } from 'nuxt/app';
import { setErrorNotification } from '~~/common/utils/helper';
import type { Schema } from '~~/common/types/api';
import type { PostTranslationPluginProvides } from '~~/common/plugins/post-translation';
import type { DatePluginProvides } from '~~/common/plugins/date';

type DeliveryTimeframeType = Schema<'DeliveryTimeFrameModel'>;
interface TimeframeOption {
  id: DeliveryTimeframeType['id']
  name: string
}

type TranslationFunction = PostTranslationPluginProvides['awt'];
type DateFormatFunction = DatePluginProvides['date'];

export const DEFAULT_TIMEFRAME_ID = 'default';

/**
 * Narrows an unknown error to a cold-parcel 428, the case `updateCheckoutData`
 * surfaces via its `error` field. Needed because that field is now a typed
 * union (`fetchOrder` result) with no common `status`/`response` members.
 */
function isColdParcel428Error (error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  if (!('status' in error) || error.status !== 428) {
    return false;
  }
  if (!('response' in error) || !error.response || typeof error.response !== 'object') {
    return false;
  }
  if (!('data' in error.response) || !error.response.data || typeof error.response.data !== 'object') {
    return false;
  }
  return 'scope' in error.response.data && error.response.data.scope === 'coldParcel';
}

/**
 * Formats timeframes for select dropdown options
 * @param timeframes - Array of timeframe objects
 * @param $awt - Translation function
 * @param $date - Date formatting function
 * @param defaultOptionKey - Translation key for the default option
 * @returns Array of formatted select options
 */
export function formatTimeframesForSelect (
  timeframes: DeliveryTimeframeType[] | null | undefined,
  $awt: TranslationFunction,
  $date: DateFormatFunction,
  defaultOptionKey: string = 'aw.common.choose_time_frame',
): TimeframeOption[] {
  if (!timeframes?.length) {
    return [];
  }

  const defaultOption: TimeframeOption = {
    id: DEFAULT_TIMEFRAME_ID,
    name: $awt(defaultOptionKey),
  };

  const timeframeOptions: TimeframeOption[] = timeframes.map(timeframe => {
    const date = new Date(timeframe.date);
    const month = $date(date, { month: 'long' });
    const day = date.getDate();
    const dayOfWeek = $date(date, { weekday: 'long' });
    const timeRange = `${timeframe.displayFrom}-${timeframe.displayTo}`;

    return {
      id: timeframe.id,
      name: `${month} ${day}, ${dayOfWeek} ${timeRange}`,
    };
  });

  return [defaultOption, ...timeframeOptions];
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export async function saveSelectedTimeframe (selectedTimeframe: TimeframeOption, timeframes: DeliveryTimeframeType[], loadingId: string, callback: Function): Promise<void> {
  const userInterfaceStore = useUserInterfaceStore();
  const checkoutStore = useCheckoutStore();
  const deliveryStore = useDeliveryStore();
  const nuxtApp = useNuxtApp();
  const { $logger } = nuxtApp;

  if (!selectedTimeframe || selectedTimeframe.id === DEFAULT_TIMEFRAME_ID) {
    return;
  }

  // Find the selected timeframe object
  const selectedTimeframeObj = timeframes.find(tf => tf.id === selectedTimeframe.id);
  if (!selectedTimeframeObj) {
    return;
  }

  const { type, id, from, to } = selectedTimeframeObj;

  userInterfaceStore.startLoading({ id: loadingId });

  try {
    const result = await checkoutStore.updateShopCheckout({
      isErrorPopupRequested: false,
      data: {
        reservation: {
          type,
          id,
          from,
          to,
        },
      },
      type: 'FOOD',
    });

    if (result.status) {
      nextTick(async () => {
        deliveryStore.selectTimeframe(selectedTimeframeObj);
        await checkoutStore.fetchCheckout({
          type: 'FOOD',
          canStartSecondAction: true,
          canRunSimultaneous: true,
        });
        await callback();
      });
    } else {
      if (isColdParcel428Error(result.error)) {
        await callback();
        setErrorNotification({
          nuxtApp: nuxtApp,
          error: result.error,
          manualClose: false,
          closable: true,
          forceModal: false,
          fromSelfcare: false,
          enableTitleMerge: true,
          fromModal: true,
        });

      } else {
        $logger.error('Failed to save timeframe:', result.error);
      }
    }
  } catch (error) {
    $logger.error('Error saving timeframe:', error);
  } finally {
    userInterfaceStore.endLoading({ id: loadingId });
  }
}
